// covered-by: frontends/carrie/src/ask-v2/rail/__tests__/groupRailCitations.test.ts

/**
 * The rail's evidence, split by what KIND of evidence it is.
 *
 * One list called "Cited Claims" held three different things at once: registry
 * rules that GOVERN the wording, conversations where this enterprise already
 * DISCUSSED it, and memories that RECORDED what was decided. A reader had to
 * tell them apart from the chip text, and the two that are not rules had no
 * link at all -- a graph citation drew a chip and went nowhere.
 *
 * The split is read off data warhol already ships. A graph citation carries
 * its `edges` (`store/graph/serve.py`), and an edge into `claim:<ruleId>`
 * names its source: `chat:<id>` means that conversation cited the rule,
 * `memory:<id>` means a recorded memory did. Measured on the
 * warhol-review-kdb epic (chat 6ab31f2d): three graph citations carrying four
 * memory edges, one of them onto the very rule the answer was about.
 */

import { type TicketMessageCitation } from "@mg/schemas/src/commons/ticket";

import { CHAT_NODE, FINDING_NODE, MEMORY_NODE } from "../../utils/graphNodes";

export interface RailChatCitation {
  /** The chat's own id, ready to link with. */
  id: string;
  /** Its title, as the graph recorded it. */
  title: string;
}

export interface RailMemoryCitation {
  /** The memory's graph `node_id`. Not the Mongo `_id` the memories API
   * exposes -- these are different identifiers, so this is a key, never a
   * link target. */
  id: string;
  /** The memory itself, which is also what the Memories tab can search for.
   * Never blank: an id alone is not something a reader can open or search. */
  text: string;
}

export interface RailFindingCitation {
  /** `<ticketId>:<commentId>`, unique per finding. */
  id: string;
  /** The finding's text, as the graph recorded it. */
  text: string;
}

export interface RailCitationGroups {
  /** Registry rules the answer stood on. What `CitedClaimsPanel` draws. */
  claims: TicketMessageCitation[];
  /** Registry rules the answer did NOT cite (`related`): the nearest rows warhol
   * fills the panel with. Shown apart, as "Related claims". */
  related: TicketMessageCitation[];
  chats: RailChatCitation[];
  memories: RailMemoryCitation[];
  /** Past review findings that rested on a cited rule. */
  findings: RailFindingCitation[];
}

interface GraphEdge {
  src?: string;
  srcLabel?: string;
}

/**
 * Split the focused answer's citations into claims, chats and memories.
 *
 * A record with a `ruleId` is a claim and is passed through untouched, so the
 * existing panel keeps drawing exactly what it drew before. Everything else is
 * mined for provenance; a graph citation that names neither a chat nor a
 * memory contributes nothing rather than an empty row. "Names" is literal for
 * a memory and a finding, which have no link of their own: without a label
 * there is nothing on the row, so there is no row.
 */
export function groupRailCitations(
  citations: readonly unknown[] | undefined,
): RailCitationGroups {
  const claims: TicketMessageCitation[] = [];
  const related: TicketMessageCitation[] = [];
  const chats = new Map<string, RailChatCitation>();
  const memories = new Map<string, RailMemoryCitation>();
  const findings = new Map<string, RailFindingCitation>();

  for (const citation of citations ?? []) {
    const record = citation as {
      ruleId?: string;
      related?: boolean;
      edges?: unknown[];
    };
    if (record?.ruleId) {
      (record.related === true ? related : claims).push(
        citation as TicketMessageCitation,
      );
      continue;
    }
    if (!Array.isArray(record?.edges)) continue;
    for (const raw of record.edges) {
      const edge = raw as GraphEdge;
      const src = String(edge?.src ?? "");
      const label = String(edge?.srcLabel ?? "").trim();
      // First label wins: the traversal returns its strongest edge first, and
      // a later one for the same node carries the same node's label anyway.
      // For the kinds that NEED a label, the first NAMED edge wins instead.
      if (src.startsWith(CHAT_NODE)) {
        // A chat is kept unnamed: its id IS the link target, and the row falls
        // back to "Untitled conversation".
        const id = src.slice(CHAT_NODE.length);
        if (id && !chats.has(id)) chats.set(id, { id, title: label });
      } else if (src.startsWith(MEMORY_NODE)) {
        const id = src.slice(MEMORY_NODE.length);
        // A memory needs its label, like a finding does. Its id is a graph node
        // id and never a link target, so an unnamed memory is a row with
        // nothing to show and no term to search the Memories tab for. warhol
        // emits exactly that when a note carries no label (`_line(None)` is
        // "", `store/graph/serve.py`), and its own answer text skips those
        // edges too (`memory_citations.py::state_memories`). `AskV2RightRail`
        // dropped the blank row at render, but `hasDrawableCitations`
        // (`collectRailContent.ts`) counted it: an answer whose only provenance
        // was an unnamed memory read as drawable, parked the citation picker on
        // itself, and opened the Context section -- on a chat whose only rail
        // content was its evidence, the whole 320px rail -- over an empty body.
        if (id && label && !memories.has(id)) {
          memories.set(id, { id, text: label });
        }
      } else if (src.startsWith(FINDING_NODE)) {
        const id = src.slice(FINDING_NODE.length);
        if (id && label && !findings.has(id)) {
          findings.set(id, { id, text: label });
        }
      }
    }
  }

  return {
    claims,
    related,
    // Array.from, not a spread: this tsconfig target does not permit
    // iterating a Map iterator directly (TS2802).
    chats: Array.from(chats.values()),
    memories: Array.from(memories.values()),
    findings: Array.from(findings.values()),
  };
}
